<template>
  <el-dialog
    :visible="visible"
    :close-on-click-modal="false"
    :modal-append-to-body="false"
    :before-close="close"
    title="Convert Attribute Type"
    width="600px"
    v-if="model"
  >
    <el-form :disabled="previewRunning">
      <div
        v-if="isCurrentTypeUnenforced"
        v-html="$t('DataManager_Tags_EnforceType_Message')"
        class="info"
      />

      <form-item label="Select type">
        <tag-type-select
          v-model="type"
          placeholder="Select data type"
          :show-example="true"
          :filtered-types="availableConversionTypes"
        />
      </form-item>

      <el-alert
        v-if="savingRules"
        type="info"
        :closable="false"
        show-icon
        title="Saving data"
        class="rules"
      >
        <span v-html="savingRules" />
      </el-alert>

      <el-alert
        v-if="displayRules"
        type="info"
        :closable="false"
        show-icon
        title="Displaying data"
        class="rules"
      >
        <span v-html="displayRules" />
      </el-alert>

      <div v-if="previewAvailable">
        <form-item
          label="Conversion Preview (Profile Attributes)"
          v-if="previewStats.profile"
        >
          <attribute-conversion-stats
            :stats="previewStats.profile"
            :examples="previewExamples.profile"
            :file-id="previewFiles.profile.id"
          />
        </form-item>

        <form-item
          label="Conversion Preview (Device Attributes)"
          v-if="previewStats.device"
        >
          <attribute-conversion-stats
            :stats="previewStats.device"
            :examples="previewExamples.device"
            :file-id="previewFiles.device.id"
          />
        </form-item>
      </div>
    </el-form>

    <span slot="footer" class="dialog-footer" v-if="type !== null">
      <el-button
        v-if="isCurrentTypeUnenforced && type === TagType.STRING"
        @click="convert"
        type="primary"
      >
        Keep as String
      </el-button>

      <el-button v-else-if="previewAvailable" @click="convert" type="primary">
        Convert To {{ $t(TagTypeLabels[type]) }}
      </el-button>

      <el-button
        v-else-if="type !== null"
        @click="preview"
        type="primary"
        plain
        :loading="previewRunning"
      >
        Preview Conversion To {{ $t(TagTypeLabels[type]) }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import FormItem from '../../../common/components/form/FormItem'
import { TagType, TagTypeLabels, TagTypeInfo } from '../enum/TagTypeEnum'
import RestApi from '../../../core/services/RestApi'
import { projectEndpoint, projectRoute } from '../../../core/helpers'
import TagTypeSelect from './TagTypeSelect'
import AttributeConversionStats from './AttributeConversionStats'

export default {
  name: 'AttributeConversionDialog',

  components: { AttributeConversionStats, TagTypeSelect, FormItem },

  data() {
    return {
      model: null,
      type: null,
      previewTasks: {},
      pollTimer: null,
      TagType,
      TagTypeLabels
    }
  },

  created() {
    this.pollTimer = setInterval(() => {
      this.pollPreviewTasks()
    }, 2500)
  },

  destroyed() {
    clearInterval(this.pollTimer)
  },

  computed: {
    savingRules() {
      if (this.type !== null) {
        return this.$t('DataManager_Tags_EnforceType_Message_Type_' + this.type)
      } else {
        return null
      }
    },

    displayRules() {
      if (this.type !== null) {
        return this.$t(
          'DataManager_Tags_EnforceType_Message_FormatType_' + this.type
        )
      } else {
        return null
      }
    },

    availableConversionTypes() {
      switch (this.model.type) {
        case TagType.STRING:
          return [
            TagType.NUMBER,
            TagType.INTEGER,
            TagType.BOOLEAN,
            TagType.DATE,
            TagType.DATETIME,
            TagType.ARRAY,
            TagType.OBJECT
          ]

        case TagType.UNENFORCED_NUMBER:
          return [
            TagType.STRING,
            TagType.NUMBER,
            TagType.INTEGER,
            TagType.BOOLEAN,
            TagType.DATE,
            TagType.DATETIME
          ]

        case TagType.UNENFORCED_DATE:
          return [TagType.STRING, TagType.DATE, TagType.DATETIME]

        case TagType.UNENFORCED_JSON:
          return [TagType.STRING, TagType.ARRAY, TagType.OBJECT]

        case TagType.BOOLEAN:
          return [TagType.STRING, TagType.INTEGER]

        case TagType.NUMBER:
          return [
            TagType.STRING,
            TagType.INTEGER,
            TagType.BOOLEAN,
            TagType.DATE,
            TagType.DATETIME
          ]

        case TagType.INTEGER:
          return [
            TagType.STRING,
            TagType.NUMBER,
            TagType.BOOLEAN,
            TagType.DATE,
            TagType.DATETIME
          ]

        case TagType.DATE:
          return [TagType.STRING, TagType.DATETIME]

        case TagType.DATETIME:
          return [TagType.STRING, TagType.DATE]

        case TagType.ARRAY:
          return [TagType.STRING]

        case TagType.OBJECT:
          return [TagType.STRING]
      }
    },

    isCurrentTypeUnenforced() {
      return TagTypeInfo[this.model.type].unenforced
    },

    previewAvailable() {
      if (this.previewTasks[this.type]) {
        return this.previewTasks[this.type].status === 2
      } else {
        return false
      }
    },

    previewRunning() {
      if (this.previewTasks[this.type]) {
        return this.previewTasks[this.type].status < 2
      } else {
        return false
      }
    },

    previewStats() {
      if (this.previewTasks[this.type]) {
        return this.previewTasks[this.type].task.stats
      } else {
        return null
      }
    },

    previewExamples() {
      if (this.previewTasks[this.type]) {
        return this.previewTasks[this.type].task.examples
      } else {
        return null
      }
    },

    previewFiles() {
      if (this.previewTasks[this.type]) {
        return this.previewTasks[this.type].task.files
      } else {
        return null
      }
    }
  },

  methods: {
    open(model) {
      this.reset(model)
      this.visible = true
    },

    close() {
      this.visible = false
      this.reset(null)
    },

    reset(model) {
      this.previewTasks = {}
      this.type = null
      this.model = model
    },

    convert() {
      this.$confirm(
        'This would start a data migration task, which might take up to several minutes depending on the volume of data. Note, the attribute will become inconsistent for use in campaigns, segments and exports for the time of migration.',
        'Warning',
        {
          confirmButtonText: 'Proceed',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      )
        .then(() => {
          RestApi(
            projectEndpoint({
              method: 'post',
              url: 'tags/' + this.model.id + '/convert',
              data: {
                type: this.type,
                preview: false
              }
            })
          )
            .then(({ data }) => {
              this.$router.push(
                projectRoute({
                  name: 'TaskHistoryView',
                  query: { id: data.id }
                })
              )
            })
            .catch(error => {
              this.close()
              this.$message({
                message: error.message,
                type: 'error'
              })
            })
        })
        .catch(() => {
          this.close()
        })
    },

    preview() {
      if (this.type !== null) {
        RestApi(
          projectEndpoint({
            method: 'post',
            url: 'tags/' + this.model.id + '/convert',
            data: {
              type: this.type,
              preview: true
            }
          })
        )
          .then(({ data }) => {
            this.$set(this.previewTasks, this.type, data)
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
          })
      }
    },

    pollPreviewTasks() {
      Object.keys(this.previewTasks).forEach(type => {
        let task = this.previewTasks[type]

        if (task.status < 2) {
          let endpoint = projectEndpoint({
            url: `task-histories/${task.id}?expand=task`
          })

          RestApi.get(endpoint.url).then(({ data }) => {
            this.$set(this.previewTasks, type, data)

            switch (task.status) {
              case 3:
                this.$message({
                  message: this.$t('Audiences_Segment_Alert_Error'),
                  type: 'error'
                })
                break

              case 4:
                this.$message({
                  message: this.$t('Audiences_Segment_Alert_Fatal'),
                  type: 'error'
                })
                break
            }
          })
        }
      })
    }
  }
}
</script>

<style scoped>
.info {
  line-height: 26px;
  margin-bottom: 16px;
}

>>> .el-dialog__body {
  padding: 10px 20px;
}

.rules {
  margin-bottom: 16px;
}

.dialog-footer {
  display: flex;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// src/modules/data-manager/components/AttributeConversionDialog.vue